import { useTranslate } from '@tolgee/react';
import { useInViewport, useRequest } from 'ahooks';
import { getNegativeItemOverview } from './service';
import { message, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useEffect, useRef } from 'react';
import ItemTableContent from './ItemTableContent';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import SafeImage from 'components/SafeImage';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { initEcharts, applyChartTheme } from 'lib/echarts';
import styled from '@emotion/styled';
import { formatNumber } from 'utils/numberFormat';

function NegativeItem({ filter }: { filter: ReviewAnalysis.OverviewFilter }) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { config } = useStoreNameConfig();

  const { loading: overviewLoading, data: { negativeItemData, otherPercent } = {} } = useRequest(
    () => getNegativeItemOverview(filter),
    {
      refreshDeps: [filter],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const chartDomRef = useRef(null);

  const [inViewport] = useInViewport(chartDomRef);

  useEffect(() => {
    if (!inViewport || !chartDomRef.current || !negativeItemData) {
      return;
    }

    const chart = initEcharts(chartDomRef.current);

    const data = negativeItemData.map(({ negativeReviewPercent, itemData: { name } }) => ({
      value: Number(negativeReviewPercent),
      name,
    }));
    data.push({ value: Number(otherPercent), name: t('aide.其他') });

    chart.setOption(
      applyChartTheme({
        legend: {
          show: false,
        },
        tooltip: {
          trigger: 'item',
          formatter: '{b}<br />{d}%',
          position: 'right',
        },
        color: ['#D44755', '#F07252', '#FBA364', '#FED186', '#FBF9B4', '#D3D3D3'],
        series: [
          {
            type: 'pie',
            radius: ['80%', '95%'],
            label: {
              show: false,
              position: 'center',
            },
            labelLine: {
              show: false,
            },
            data: data.map(({ value, name }) => {
              return {
                value,
                name,
                label: {
                  overflow: 'break',
                },
              };
            }),
          },
        ],
      }),
    );

    const handler = () => {
      chart.resize();
    };

    window.addEventListener('resize', handler);

    return () => {
      window.removeEventListener('resize', handler);

      chart.dispose();
    };
  }, [inViewport, negativeItemData, otherPercent, t]);

  if (!negativeItemData) {
    return overviewLoading ? <LoadingOutlined /> : <></>;
  }

  return (
    <Container>
      <h3>{t('TOP5差评商品')}</h3>
      <section>
        <span ref={chartDomRef} />
        <section>
          <div>TOP5</div>
          <div>{t('差评商品占比')}</div>
        </section>
        <Table
          size="small"
          loading={overviewLoading}
          dataSource={negativeItemData}
          columns={[
            {
              key: 'index',
              title: t('排名'),
              width: 60,
              render: (_, __, index) => index + 1,
            },
            {
              key: 'item',
              title: t('商品'),
              width: 360,
              ellipsis: true,
              render: (_, { itemData: { img, name, platform, region, storeName, storeAlias } }) => {
                return (
                  <ItemContainer>
                    <SafeImage width={56} height={56} src={img} />
                    <section>
                      <p>{name}</p>
                      <div>
                        {platformLabelMap[toLowerCase(platform)]}-{getRegionLabel(region)}-
                        {isStoreAliasFirst(config, platform, region)
                          ? storeAlias || storeName
                          : storeName}
                      </div>
                    </section>
                  </ItemContainer>
                );
              },
            },
            {
              key: 'count',
              title: t('差评数'),
              width: 100,
              render: (_, { negativeReviewCount }) => formatNumber(negativeReviewCount, '0,0'),
            },
          ]}
        />
      </section>
      {negativeItemData && (
        <>
          <h3>{t('商品差评明细')}</h3>
          <ItemTableContent isItemDistribution filter={filter} />
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 16px 24px;
  background: var(--fett-bg-panel);
  > h3 {
    position: relative;
    font-size: 16px;
    text-indent: 12px;
    &::before {
      content: '';
      display: inline-block;
      position: absolute;
      top: 4px;
      left: 0;
      width: 4px;
      height: 16px;
      background: var(--ant-primary-color);
    }
  }
  > section {
    display: flex;
    position: relative;
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: solid 1px #f0f2f5;
    > span {
      flex: 0 0 auto;
      width: 250px;
      height: 250px;
      margin: 0 50px;
      z-index: 1;
    }
    > div {
      flex: 1 0 0;
    }
    > section {
      width: 250px;
      height: 250px;
      margin: 0 50px;
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 16;
      font-weight: 500;
      z-index: 0;
    }
  }
`;

const ItemContainer = styled.div`
  display: flex;
  > div {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      margin-bottom: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    > div {
      font-size: 12px;
      color: #84868c;
    }
  }
`;

export default NegativeItem;
